import { useContext, useEffect, useMemo, useState } from 'react';
import {
    PageWrapper,
    ControlsRow,
    TabsContainer,
    TabButton,
    IframeWrapper,
    Loader,
    ContentContainer,
} from './styles';
import { fetchDashboardEmbed } from './api';
import { DashboardType } from './types';

import { MPosContext } from 'utils/context';
import SalesDropDown from 'components/SalesReport/SalesDropDown';
import { useSegmentViewEvents } from 'hooks/useSegmentView';
import {
    PRODUCT,
    SegmentEventActionTypes,
    SegmentEventNameTypes,
    SegmentEventPages,
    SegmentEventPageTypes,
    SUB_PRODUCT_NAME,
} from 'utils/segment/constants';
import { useViewport } from 'utils/responsive';
import sendSegmentEvent from 'utils/segment';
import { toSnakeCase } from 'utils/date';

const SalesReport = () => {
    const { permissions, partnerId, userDetails } = useContext(MPosContext);
    const [dashboardTypes, setDashboardTypes] = useState<DashboardType[]>([]);
    const [activeDashboardId, setActiveDashboardId] = useState<number | null>(
        null
    );
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [embedUrl, setEmbedUrl] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [isInit, setIsInit] = useState(0);
    const { isMobile } = useViewport();

    useSegmentViewEvents(SegmentEventNameTypes.VIEW, {
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_type: SegmentEventPageTypes.MAIN_PAGE,
        view_name: SegmentEventPages.VIEW_REPORT_DASHBORD,
        product: PRODUCT,
        sub_product_name: SUB_PRODUCT_NAME,
        platform: isMobile ? 'mweb' : 'web',
        referrer: document.referrer || '',
        device: navigator.userAgent || '',
        partner: partnerId,
    });

    /* Initial Load */
    useEffect(() => {
        const init = async () => {
            setLoading(true);

            const res = await fetchDashboardEmbed(partnerId, {
                group: permissions.group,
            });

            const types =
                res.user_dashboard_config.dashboard_page.dashboard_types;

            setDashboardTypes(types);
            setActiveDashboardId(types[0].id);

            sendSegmentEvent(
                SegmentEventNameTypes.ACTION,
                {
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: toSnakeCase(types[0].name),
                },
                { userDetails, isMobile }
            );

            setIsInit(1);
            setEmbedUrl(res.url);
            setLoading(false);
        };

        init();
    }, []);

    const activeDashboard = useMemo(
        () => dashboardTypes.find((d) => d.id === activeDashboardId),
        [dashboardTypes, activeDashboardId]
    );

    /* Reset filters on dashboard change */
    useEffect(() => {
        if (!activeDashboard) return;

        const defaults: Record<string, string> = {};
        activeDashboard.filters.forEach((f) => {
            defaults[f.id] = f.default_value;
        });

        setFilters(defaults);
    }, [activeDashboard]);

    /* Fetch embed on tab / filter change */
    useEffect(() => {
        if (!activeDashboardId) return;

        const fetchEmbed = async () => {
            setLoading(true);

            const res = await fetchDashboardEmbed(partnerId, {
                group: permissions.group,
                dashboard: activeDashboardId,
                ...filters,
            });

            setEmbedUrl(res.url);
            setLoading(false);
        };
        if (isInit > 2) {
            fetchEmbed();
        } else {
            setIsInit(isInit + 1);
        }
    }, [activeDashboardId, filters]);

    const onChangeTabHandler = (d: DashboardType) => {
        setActiveDashboardId(d.id);
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: toSnakeCase(d.name),
            },
            { userDetails, isMobile }
        );
    };

    return (
        <PageWrapper>
            {/* Tabs */}
            <TabsContainer>
                {dashboardTypes.map((d) => (
                    <TabButton
                        key={d.id}
                        active={d.id === activeDashboardId}
                        onClick={() => onChangeTabHandler(d)}
                    >
                        {d.name}
                    </TabButton>
                ))}
            </TabsContainer>
            <ContentContainer>
                {/* Filters */}
                {activeDashboard && activeDashboard?.filters.length > 0 && (
                    <ControlsRow>
                        {activeDashboard.filters.map((filter) => (
                            <>
                                <SalesDropDown
                                    options={filter.data_source.options}
                                    onChange={(value) =>
                                        setFilters({
                                            ...filters,
                                            [filter.id]: value,
                                        })
                                    }
                                    value={filters[filter.id]}
                                />
                            </>
                        ))}
                    </ControlsRow>
                )}

                {/* Dashboard */}
                {loading ? (
                    <Loader>Loading dashboard...</Loader>
                ) : (
                    <IframeWrapper>
                        <iframe
                            title="report-dashboard"
                            src={embedUrl}
                            width="100%"
                            height="100%"
                            frameBorder={0}
                        />
                    </IframeWrapper>
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default SalesReport;
